import { RECEIVED_MSG, RECEIVED_READ_MSG } from "@genbaryoku/contract";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { messageOf } from "../api.js";
import { usePinchZoom } from "../pages/schedule/hooks.js";
import { FitWidthIcon, OpenIcon } from "./icons.jsx";

/**
 * 原本のページの画像を縦に並べて見せる（倍率・幅に合わせる・P.n／m）。受け取った書類の AI読み取り結果の引き出し（../eigyouryoku/docs/13 §4.5）と、
 * 外注見積の詳細の画面（#198、docs/79 §13 決定 C）が同じものを使う（2026-10-17 に kits/received/OriginalPane.tsx から取り出した。動きは同じ）。
 *   上にツールバー：「−」「100%」「＋」（10% 刻み・50〜300%）「幅に合わせる」（既定・is-on）／右に「P.n／m」（スクロールで今のページ）・extra・「原本表示」（新しいタブ）
 *   ⌘／Ctrl＋ホイールで拡大縮小（ホイールだけは普通のスクロール）・ダブルクリックで 幅に合わせる ⇄ 100%・タッチは2本指（1本指はスクロール）
 *   「幅に合わせる」のときは枠の幅に追従する（境を動かすと大きくなる）。倍率の持ち主は使う側（zoom・onZoom）
 *   compact（幅 767px 以下）＝ツールバーは −・倍率・＋・幅に合わせる だけ
 *   testid は使う側が決められる（既定は受け取った書類の名前＝前のまま）
 */
export const ZOOM_MIN = 50;
export const ZOOM_MAX = 300;
const ZOOM_STEP = 10;
/** 100%＝ページの長辺 1600px の画像を 0.7 倍（A4 縦なら幅 約 790px＝紙の原寸に近い） */
const BASE = 0.7;
/** ページの幅（px）が分からないとき（A4 縦の 1600px の画像） */
const PAGE_W = 1131;
/** 倍率：数字（%）か「幅に合わせる」 */
export type Zoom = number | "fit";
export type ViewerPage = { n: number; url: string; width: number | null; height: number | null };

const clamp = (z: number) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(z)));

const DEFAULT_IDS = {
  root: "received-original",
  toolbar: "rd-toolbar",
  zoomOut: "rd-zoom-out",
  zoom: "rd-zoom",
  zoomIn: "rd-zoom-in",
  fit: "rd-fit",
  page: "rd-page",
  open: "received-open-original",
  pages: "received-pages",
  preparing: "received-pages-preparing",
  img: "received-page",
};
export type ViewerIds = typeof DEFAULT_IDS;

export function PageViewer({
  docKey,
  title,
  pages,
  error,
  pageCount,
  openUrl,
  zoom,
  onZoom,
  compact = false,
  extra,
  onPage,
  ids: idsIn,
  label = RECEIVED_READ_MSG.original,
  jump,
}: {
  /** 書類が替わったら P.1 に戻す */
  docKey: string;
  /** 画像の代わりの文（「{title} の n ページ目」） */
  title: string;
  /** undefined＝読み込み中（ページの画像を作っている最中を含む） */
  pages: ViewerPage[] | undefined;
  error?: unknown;
  pageCount?: number | null;
  /** 「原本表示」の先（無ければ出さない） */
  openUrl?: string | null;
  zoom: Zoom;
  onZoom: (z: Zoom) => void;
  compact?: boolean;
  /** ツールバーの右（「原本表示」の左）に置くもの＝境の切替「原本を広く／結果を広く」 */
  extra?: ReactNode;
  /** 今のページ（スクロールで変わる）と全部のページ数 */
  onPage?: (n: number, m: number) => void;
  ids?: Partial<ViewerIds>;
  label?: string;
  /** そのページへ移る（外注見積の結果の行の「P.n」。at が変わるたびに移る） */
  jump?: { n: number; at: number } | null;
}) {
  const ids = { ...DEFAULT_IDS, ...idsIn };
  const list = pages ?? [];
  const m = list.length || pageCount || 1;
  const base = (list[0]?.width ?? PAGE_W) * BASE;

  // 幅に合わせたときの倍率（枠の幅 ÷ 100% の幅）。枠の大きさが変わるたびに測り直す
  const box = useRef<HTMLDivElement>(null);
  const [fitPct, setFitPct] = useState(100);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // 幅に合わせたときは本当の倍率を出す（50% より小さいこともある＝枠が狭いスマホ。「＋」「−」で選べるのは 50〜300%）
    const ro = new ResizeObserver(() => setFitPct(Math.max(1, Math.round(((el.clientWidth - 24) / base) * 100))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [base]);
  const pct = zoom === "fit" ? fitPct : zoom;
  const pctRef = useRef(pct);
  pctRef.current = pct;
  const step = (dir: 1 | -1) => {
    const now = pctRef.current;
    // 幅に合わせた半端な倍率（63%）からは、次の 10% の刻み（70%／60%）へ
    const next = dir > 0 ? Math.floor(now / ZOOM_STEP) * ZOOM_STEP + ZOOM_STEP : Math.ceil(now / ZOOM_STEP) * ZOOM_STEP - ZOOM_STEP;
    onZoom(clamp(next));
  };
  const stepRef = useRef(step);
  stepRef.current = step;

  // ⌘／Ctrl＋ホイール（passive にしない＝ブラウザの画面の拡大を止める）
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      if (e.deltaY !== 0) stepRef.current(e.deltaY < 0 ? 1 : -1);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);
  // タッチは2本指（1本指はスクロール）
  const onPinch = useCallback((z: number) => onZoom(clamp(z)), [onZoom]);
  usePinchZoom(box, pct, onPinch, 0, ZOOM_MIN, ZOOM_MAX);

  // 今のページ（枠の上から 1/3 の所にあるページ）
  const [page, setPage] = useState(1);
  const onScroll = () => {
    const el = box.current;
    if (!el) return;
    const imgs = el.querySelectorAll<HTMLImageElement>("img.rdp-page");
    const line = el.scrollTop + el.clientHeight / 3;
    let n = 1;
    imgs.forEach((img, i) => {
      if (img.offsetTop <= line) n = i + 1;
    });
    if (n !== page) setPage(n);
  };
  useEffect(() => {
    setPage(1);
    box.current?.scrollTo({ top: 0 });
  }, [docKey]);
  useEffect(() => {
    onPage?.(page, m);
  }, [page, m, onPage]);
  useEffect(() => {
    if (!jump) return;
    const el = box.current;
    const img = el?.querySelectorAll<HTMLImageElement>("img.rdp-page")[jump.n - 1];
    if (el && img) el.scrollTo({ top: img.offsetTop - 8 });
  }, [jump?.at]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rdp" data-testid={ids.root}>
      <div className="rdp-bar" role="toolbar" aria-label={label} data-testid={ids.toolbar}>
        <button type="button" className="rdp-tb" aria-label={RECEIVED_READ_MSG.zoomOut} disabled={pct <= ZOOM_MIN} onClick={() => step(-1)} data-testid={ids.zoomOut}>
          −
        </button>
        <span className="rdp-pct num" aria-label={RECEIVED_READ_MSG.zoomLabel} aria-live="polite" data-testid={ids.zoom}>
          {pct}%
        </span>
        <button type="button" className="rdp-tb" aria-label={RECEIVED_READ_MSG.zoomIn} disabled={pct >= ZOOM_MAX} onClick={() => step(1)} data-testid={ids.zoomIn}>
          ＋
        </button>
        {/* アイコンだけ（言葉は aria-label と title）。押されている形は is-on＝淡い藍の地 */}
        <button
          type="button"
          className={"rdp-tb rdp-ico" + (zoom === "fit" ? " is-on" : "")}
          aria-pressed={zoom === "fit"}
          aria-label={RECEIVED_READ_MSG.fitWidth}
          title={RECEIVED_READ_MSG.fitWidth}
          onClick={() => onZoom("fit")}
          data-testid={ids.fit}
        >
          <FitWidthIcon />
        </button>
        <span className="sp" />
        {compact ? null : (
          <>
            <span className="rdp-page num" data-testid={ids.page}>
              P.{page}／{m}
            </span>
            {extra}
            {openUrl ? (
              <a className="rdp-tb rdp-open" href={openUrl} target="_blank" rel="noopener" title={RECEIVED_MSG.openOriginal} data-testid={ids.open}>
                <OpenIcon />
                <span className="rdp-open-t">{RECEIVED_MSG.openOriginal}</span>
              </a>
            ) : null}
          </>
        )}
      </div>
      <div className="rdp-scroll" ref={box} onScroll={onScroll} onDoubleClick={() => onZoom(zoom === "fit" ? 100 : "fit")} data-testid={ids.pages}>
        {error ? (
          <p className="shu rdp-msg">{messageOf(error)}</p>
        ) : !pages ? (
          <p className="hint rdp-msg" data-testid={ids.preparing}>
            {RECEIVED_MSG.preparing}
          </p>
        ) : list.length === 0 ? (
          <p className="hint rdp-msg">{RECEIVED_MSG.noPages}</p>
        ) : (
          <div className={"rdp-pages" + (zoom === "fit" ? " is-fit" : "")}>
            {list.map((p) => (
              <img
                key={p.n}
                className="rdp-page"
                src={p.url}
                alt={RECEIVED_MSG.pageAlt(title, p.n)}
                width={p.width ?? undefined}
                height={p.height ?? undefined}
                style={zoom === "fit" ? undefined : { width: Math.round((p.width ?? PAGE_W) * BASE * (zoom / 100)) }}
                loading={p.n > 2 ? "lazy" : "eager"}
                draggable={false}
                data-testid={ids.img}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
